// v16.93 (06/2026) — OFFLINE-FIRST referee mutations (thay connection lock).
// Mục tiêu user: trọng tài điều hành XUYÊN SUỐT trận đấu dù mất mạng /
// backend nghẽn (bấm điểm, đổi sân, hoàn tác, kết thúc trận, tung xu...).
// Khi có kết nối lại → refereeSyncEngine replay tuần tự FIFO với CÙNG
// Idempotency-Key (BE dedupe qua `referee_idempotency`).
//
// Flow:
//   1. navigator.onLine=false HOẶC queue của trận còn pending → enqueue
//      IndexedDB NGAY (giữ FIFO nghiêm ngặt — fix gốc sự cố Karinox
//      "queue chồng chéo làm điểm nhảy loạn xạ").
//   2. Online + queue rỗng → gọi network (timeout 10s, retry 1 lần backoff 1s).
//   3. Lỗi transient sau retry → enqueue với CÙNG Idempotency-Key (server có
//      thể đã xử lý request bị timeout — dedupe an toàn khỏi double-score).
//   4. 4xx nghiệp vụ (trừ 408/425/429) → throw ngay để UI hiện lỗi thật.
import { api } from "./api";
import { enqueueEvent } from "./refereeOfflineStore";
import { noteEnqueued, hasPending } from "./refereeSyncEngine";

const TRANSIENT_HTTP = new Set([0, 408, 425, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524]);
const REQUEST_TIMEOUT_MS = 10000;
const MAX_RETRIES = 1; // = 2 lần thử tổng cộng; hết → queue offline (không khoá UI)
const RETRY_BACKOFF_MS = [1000];

function _genIdem() {
  return (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `q-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function _sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

async function _queue({ match_id, endpoint, method, body, idem }) {
  try {
    const row = await enqueueEvent({ match_id, endpoint, method, body, idem });
    noteEnqueued(row);
    return { ok: true, queued: true, offline: true, event_id: row.id };
  } catch (storeErr) {
    // IndexedDB không khả dụng (private mode hiếm gặp) — bất khả kháng.
    const err = new Error("Không thể lưu thao tác offline");
    err.connectionLost = true;
    throw err;
  }
}

export async function refereeMutate({
  match_id,
  endpoint,        // "/api/referee/matches/<mid>/score"
  method = "POST",
  body = {},
}) {
  const idem = _genIdem();
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  // Queue còn pending → mọi mutation mới PHẢI xếp sau (giữ thứ tự bấm).
  if (offline || hasPending(match_id)) {
    return _queue({ match_id, endpoint, method, body, idem });
  }

  const path = endpoint.startsWith("/api/") ? endpoint.slice(4) : endpoint;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      const res = await api.request({
        url: path,
        method,
        data: body,
        headers: { "Idempotency-Key": idem },
        timeout: REQUEST_TIMEOUT_MS,
      });
      return { ok: true, queued: false, data: res.data };
    } catch (err) {
      const status = err?.response?.status || 0;
      const isTimeout = err?.code === "ECONNABORTED";
      const transient = isTimeout || TRANSIENT_HTTP.has(status);
      // 4xx nghiệp vụ → throw ngay (400, 401, 403, 404, 409, 422...)
      if (!transient) {
        throw err;
      }
      if (attempt < MAX_RETRIES) {
        await _sleep(RETRY_BACKOFF_MS[attempt] || 1000);
        continue;
      }
      // Hết retry → queue offline với CÙNG idem key. UI KHÔNG bị khoá.
      return _queue({ match_id, endpoint, method, body, idem });
    }
  }
  return _queue({ match_id, endpoint, method, body, idem });
}
